import { Button, Select } from '@mantine/core';
import { modals } from '@mantine/modals';
import { useMemo, useState } from 'react';
import type { TestAccountType } from '@prisma/client';
import { useProjectId } from '~/hooks/context/useProjectId';
import { api } from '~/trpc-extra/api';
import { NewTestAccountForm } from '../operation/authOp/NewTestAccountForm';
import { IconBrandGoogleFilled, IconMail } from '@tabler/icons-react';
import type { SelectedTestAccount } from '~/server/logic/prisma/test-account-select';

// an independent component that allows users to select from a list of all
// available test accounts, and runs the provided onSubmit handler with the
// selected testAccountId
export function TestAccountSelect({
  onSubmit,
  title = 'Select Test Account',
  submitButtonText = 'Select',
  filter,
  accountType,
}: {
  onSubmit: (accountId: string) => void;
  title?: string;
  submitButtonText?: string;
  filter?: (account: SelectedTestAccount) => boolean;
  accountType?: TestAccountType;
}) {
  const projectId = useProjectId();
  const [accountId, setAccountId] = useState<string>('');
  const [isAddingNewAccount, setIsAddingNewAccount] = useState(false);
  const [savingAccount, setSavingAccount] = useState(false);

  const { data: accounts = [] } = api.testAccount.list.useQuery({ projectId: projectId || '' });

  const filteredAccounts = useMemo(() => (filter ? accounts.filter(filter) : accounts), [filter, accounts]);

  const selectedAccountDetails = useMemo(
    () => filteredAccounts?.find((a) => a.id === accountId),
    [filteredAccounts, accountId],
  );

  const accountSelectData = useMemo(
    () => [
      { value: 'new', label: '+ Add New Account' },
      ...(filteredAccounts?.map((a) => ({ value: a.id, label: a.name })) || []),
    ],
    [filteredAccounts],
  );

  const handleSubmit = () => {
    if (accountId) {
      modals.closeAll();
      onSubmit(accountId);
    }
  };

  return (
    <div className="flex flex-col gap-6 px-2 py-4">
      <div>
        <h2 className="text-md font-semibold text-gray-700 dark:text-gray-300">{title}</h2>
      </div>

      <div className="space-y-4">
        <div>
          <div className="text-sm font-medium">Test Account</div>
          {!isAddingNewAccount ?
            <div>
              <Select
                data={accountSelectData}
                value={accountId}
                onChange={(value) => {
                  if (value === 'new') {
                    setIsAddingNewAccount(true);
                  } else {
                    setAccountId(value || '');
                  }
                }}
                className="mt-1"
                placeholder="Select a test account..."
              />
              {!!selectedAccountDetails && (
                <div className="mt-8">
                  <div className="mb-2 text-sm font-medium opacity-50">Test Account Details</div>
                  <div className="flex items-center justify-between">
                    <div className="font-semibold">{selectedAccountDetails.name}</div>
                    {selectedAccountDetails.type === 'GOOGLE' ?
                      <IconBrandGoogleFilled
                        size={16}
                        className="text-gray-500"
                      />
                    : <IconMail
                        size={16}
                        className="text-gray-500"
                      />
                    }
                  </div>
                  {!!selectedAccountDetails.description && (
                    <div className="mt-1 text-xs text-gray-600 dark:text-gray-400">
                      {selectedAccountDetails.description}
                    </div>
                  )}
                  <div>{selectedAccountDetails.userName}</div>
                  <div className="italic">{selectedAccountDetails.password}</div>
                </div>
              )}
            </div>
          : <div className="mt-3 space-y-3">
              <NewTestAccountForm
                mutating={savingAccount}
                onMutate={() => setSavingAccount(true)}
                onSettled={() => setSavingAccount(false)}
                onSuccessSubmit={(newAccountId) => {
                  setAccountId(newAccountId);
                  setIsAddingNewAccount(false);
                }}
                onCancel={() => setIsAddingNewAccount(false)}
                accountType={accountType}
              />
            </div>
          }
        </div>
      </div>

      <div className="mt-2 flex items-center justify-end gap-2">
        <Button
          variant="default"
          onClick={() => modals.closeAll()}
        >
          Cancel
        </Button>
        <Button
          disabled={!accountId || savingAccount || isAddingNewAccount}
          onClick={handleSubmit}
        >
          {submitButtonText}
        </Button>
      </div>
    </div>
  );
}
